import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { contactApi } from '../../services/api'
import { showSuccess, showError } from './appSlice'

const initialState = {
  messages: [],
  contactInfo: {
    email: '',
    phone: '',
    location: '',
    socialLinks: [],
  },
  isLoading: false,
  isSubmitting: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
}

// Async thunks
export const sendMessage = createAsyncThunk(
  'contact/sendMessage',
  async (messageData, { dispatch, rejectWithValue }) => {
    try {
      const response = await contactApi.sendMessage(messageData)
      
      if (response.success) {
        dispatch(showSuccess('Сообщение отправлено успешно', 'Спасибо за обращение!'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка отправки сообщения')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при отправке сообщения'))
      return rejectWithValue(error.message)
    }
  }
)

export const fetchMessages = createAsyncThunk(
  'contact/fetchMessages',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { pagination } = getState().contact
      const queryParams = {
        page: pagination.page,
        limit: pagination.limit,
        ...params,
      }
      
      const response = await contactApi.getMessages(queryParams)
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки сообщений')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchContactMessages = createAsyncThunk(
  'contact/fetchContactMessages',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await contactApi.getMessages(params)
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки сообщений')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const markMessageAsRead = createAsyncThunk(
  'contact/markMessageAsRead',
  async (messageId, { dispatch, rejectWithValue }) => {
    try {
      const response = await contactApi.markAsRead(messageId)
      
      if (response.success) {
        return messageId
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления сообщения')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении сообщения'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteMessage = createAsyncThunk(
  'contact/deleteMessage',
  async (messageId, { dispatch, rejectWithValue }) => {
    try {
      const response = await contactApi.deleteMessage(messageId)
      
      if (response.success) {
        dispatch(showSuccess('Сообщение удалено'))
        return messageId
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления сообщения')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении сообщения'))
      return rejectWithValue(error.message)
    }
  }
)

export const fetchContactInfo = createAsyncThunk(
  'contact/fetchContactInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await contactApi.getContactInfo()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки контактной информации')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const contactSlice = createSlice({
  name: 'contact',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.pagination.page = action.payload
    },
    
    clearError: (state) => {
      state.error = null
    },
    
    resetSubmissionState: (state) => {
      state.isSubmitting = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Send Message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isSubmitting = true
        state.error = null
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.isSubmitting = false
        state.error = null
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSubmitting = false
        state.error = action.payload
      })
    
    // Fetch Messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false
        state.messages = action.payload.messages || []
        state.pagination = {
          ...state.pagination,
          ...action.payload.pagination,
        }
        state.error = null
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Fetch Contact Messages
    builder
      .addCase(fetchContactMessages.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchContactMessages.fulfilled, (state, action) => {
        state.isLoading = false
        state.messages = action.payload.messages || []
        state.pagination = {
          ...state.pagination,
          ...action.payload.pagination,
        }
        state.error = null
      })
      .addCase(fetchContactMessages.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Mark Message as Read
    builder
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        const messageId = action.payload
        const message = state.messages.find(msg => msg.id === messageId)
        if (message) {
          message.is_read = true
          message.read_at = new Date().toISOString()
        }
      })
    
    // Delete Message
    builder
      .addCase(deleteMessage.fulfilled, (state, action) => {
        const messageId = action.payload
        state.messages = state.messages.filter(msg => msg.id !== messageId)
        state.pagination.total = Math.max(0, state.pagination.total - 1)
      })
    
    // Fetch Contact Info
    builder
      .addCase(fetchContactInfo.fulfilled, (state, action) => {
        state.contactInfo = { ...state.contactInfo, ...action.payload }
      })
  },
})

export const {
  setPage,
  clearError,
  resetSubmissionState,
} = contactSlice.actions

// Selectors
export const selectUnreadMessages = (state) => {
  return state.contact.messages.filter(message => !message.is_read)
}

export const selectUnreadCount = (state) => {
  return state.contact.messages.filter(message => !message.is_read).length
}

export const selectRecentMessages = (state, limit = 5) => {
  return state.contact.messages
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, limit)
}

export default contactSlice.reducer